import { cn } from '@sjuni/ui';
import { Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { isVisibleTo, NAV_ITEMS } from '@/lib/nav/navigation';
import { useAuth } from '@/lib/auth/useAuth';

import { NavIcon } from './NavIcon';

/**
 * docs/05-ADMIN-DASHBOARD.md §1 — "`⌘K` / `Ctrl+K` opens a command palette
 * (jump to any record or admin action)."
 *
 * Screens today; records once the modules exist and there is a search endpoint
 * behind them. It reads the same `NAV_ITEMS` the sidebar does, filtered by the
 * same permission rule — a palette with its own list is a palette that offers a
 * screen the sidebar has stopped showing, and lands the person on a 403.
 *
 * The listbox is built from `div`s with `role="option"`. Anchors inside a
 * listbox fail `nested-interactive`, and options that are links break arrow-key
 * navigation, which is the only reason to have a palette at all. Selection
 * moves `aria-activedescendant`; focus stays in the input.
 */
export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, hasRole } = useAuth();

  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const viewer = useMemo(() => ({ can, isSuperAdmin: hasRole('super-admin') }), [can, hasRole]);

  /*
   * Matched on the *translated* label, not the key: an editor typing "ამბები"
   * is looking for News, and a palette that only matches `news` is a palette
   * for developers.
   */
  const results = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();

    return NAV_ITEMS.filter((item) => isVisibleTo(item, viewer))
      .map((item) => ({ item, label: t(`nav.items.${item.key}`) }))
      .filter(({ label }) => needle === '' || label.toLocaleLowerCase().includes(needle))
      .slice(0, 8);
  }, [query, t, viewer]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      inputRef.current?.focus();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const choose = (path: string) => {
    onClose();
    navigate(path);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();

      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();

      setActive((current) => {
        if (results.length === 0) {
          return 0;
        }

        const delta = event.key === 'ArrowDown' ? 1 : -1;

        // Wraps, so the last item is one key from the first.
        return (current + delta + results.length) % results.length;
      });

      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      const chosen = results[active];

      if (chosen) {
        choose(chosen.item.path);
      }
    }
  };

  return (
    /*
     * The backdrop closes on click. It carries no keyboard handler of its own:
     * Escape closes from the input, which is where focus already is.
     */
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(26,24,20,0.45)] p-4 pt-[12vh]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.palette.label')}
        className="w-full max-w-lg overflow-hidden rounded-panel bg-surface shadow-lg"
      >
        <div className="flex items-center gap-3 border-b border-subtle px-4">
          <Search size={18} strokeWidth={1.75} aria-hidden="true" className="text-secondary" />

          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={results[active] ? `${listId}-${active}` : undefined}
            aria-label={t('nav.palette.label')}
            placeholder={t('nav.palette.placeholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            className="h-14 flex-1 bg-transparent text-body text-primary outline-none placeholder:text-muted"
          />
        </div>

        {results.length === 0 ? (
          <p className="px-4 py-6 text-body-sm text-secondary">{t('nav.palette.empty')}</p>
        ) : (
          <ul id={listId} role="listbox" aria-label={t('nav.palette.label')} className="py-2">
            {results.map(({ item, label }, index) => (
              /* No keyboard handler on the option itself: the listbox is
                 driven from the input's arrow keys and Enter, and a second
                 path here would fight it. */
              <li
                key={item.key}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                onMouseDown={(event) => {
                  // `mousedown` rather than `click`: the input blurring first
                  // would close the palette out from under the pointer.
                  event.preventDefault();
                  choose(item.path);
                }}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  'flex cursor-pointer items-center gap-3 px-4 py-2 text-body-sm',
                  index === active ? 'bg-accent-tint-navy text-primary' : 'text-secondary',
                )}
              >
                <NavIcon name={item.icon} className="shrink-0" />
                {label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
